import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, catchError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SessionExpiredService } from '../services/session-expired.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isHandling401 = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private sessionExpiredService: SessionExpiredService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Add auth header to requests
    const authRequest = this.addAuthHeader(request);

    const hasAuthHeader = authRequest.headers.get('Authorization') ? 'YES' : 'NO';

    if (hasAuthHeader === 'YES') {
    }

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 500) {
          // Replace 500 error with user-friendly maintenance message
          const maintenanceMessage = this.translate.instant('errors.maintenance');
          const customError = new Error(maintenanceMessage);
          return throwError(() => customError);
        } else if (error.status === 401) {
          if (this.isRemoteRequest(authRequest.url)) {
            console.warn('[AUTH_INTERCEPTOR] Remote 401 detected, clearing TNC token only');
            this.authService.clearTncApiToken();
          }
          if (!this.isHandling401) {
            this.isHandling401 = true;
            this.sessionExpiredService.show();
            this.isHandling401 = false;
          }
        }
        return throwError(() => error);
      })
    );
  }

  private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    // Skip auth header for login and public endpoints
    if (this.isPublicEndpoint(request.url)) {
      return request;
    }

    // Respect an explicit Bearer token already set on the request (e.g. impersonation
    // management calls that must use the real admin token, not the impersonated one).
    if (request.headers.get('Authorization')?.startsWith('Bearer ')) {
      return request;
    }

    const authHeaders = this.authService.getAuthHeaders();

    if (authHeaders['Authorization']) {
      return request.clone({
        setHeaders: authHeaders
      });
    }

    return request;
  }

  private isPublicEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/auth/login',
      '/webapp/login',
      '/webapp/google-login',
      // Read by the login page before there is a session, so never send a (stale) token with it.
      '/webapp/auth/google-config',
      '/health',
      '/api/docs'
    ];

    if (url.includes('/assets/')) {
      return true;
    }

    return publicEndpoints.some(endpoint => url.includes(endpoint));
  }

  private isRemoteRequest(url: string): boolean {
    const remoteBase = this.authService.getTncApiBaseUrl();
    return !!remoteBase && url.startsWith(remoteBase);
  }
}
